import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { apiService } from '../services/apiService';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const location = useLocation();

  // Efecto para detectar scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cargar categorías de equipos dinámicamente
  useEffect(() => {
    const fetchCategoriasEquipos = async () => {
      setLoadingCats(true);
      try {
        const response = await apiService.getCategoriasEquipos();
        if (response.estado === 1) {
          // Filtrar solo las activas y ordenar por el campo 'orden'
          const catsActivas = response.data
            .filter(cat => cat.activo === 1)
            .sort((a, b) => a.orden - b.orden);
          setCategorias(catsActivas);
        }
      } catch (error) {
        console.error('Error cargando categorías de equipos:', error);
      } finally {
        setLoadingCats(false);
      }
    };

    fetchCategoriasEquipos();
  }, []);

  // Cerrar menús al cambiar de ruta
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: '/', label: 'Inicio' },
    { to: '/galeria', label: 'Galería' },
    { to: '/eventos', label: 'Eventos' },
    { to: '/nosotros', label: 'Nosotros' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-black/95 backdrop-blur-md shadow-2xl border-b border-yellow-600/20' 
          : 'bg-black'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          
          {/* Logo + Texto */}
          <motion.div
            className="flex items-center space-x-3 cursor-pointer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link to="/" className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-full border-2 border-yellow-500 flex items-center justify-center bg-black">
                  <img 
                    src="/images/logo22.png" 
                    alt="Rotmans Logo" 
                    className="w-8 h-8 object-contain"
                  />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
              </div>
              
              <div className="flex flex-col">
                <span className="text-2xl lg:text-3xl font-bold text-yellow-500 tracking-wide">
                  ROTMANS
                </span>
                <span className="text-xs lg:text-sm text-gray-400 tracking-widest uppercase">
                  Producciones & Eventos
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Nav Desktop */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item, index) => (
              <motion.div
                key={item.to}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={item.to}
                  className={`px-4 py-2 font-medium transition-all duration-300 relative group ${
                    location.pathname === item.to 
                      ? 'text-yellow-500' 
                      : 'text-gray-300 hover:text-yellow-500'
                  }`}
                >
                  {item.label}
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-yellow-500 transition-all duration-300 ${
                    location.pathname === item.to ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
              </motion.div>
            ))}

            {/* Dropdown Equipamiento Técnico (DINÁMICO) */}
            <div 
              className="relative"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <button
                className={`px-4 py-2 font-medium flex items-center space-x-1 transition-all duration-300 ${
                  dropdownOpen ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-500'
                }`}
              >
                <span>Equipamiento Técnico</span>
                <motion.span
                  animate={{ rotate: dropdownOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown size={16} />
                </motion.span>
              </button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-black border border-yellow-600/30 rounded-lg shadow-2xl overflow-hidden"
                  >
                    {loadingCats ? (
                      <div className="px-4 py-3 text-gray-500 text-sm text-center">Cargando...</div>
                    ) : categorias.length > 0 ? (
                      categorias.map((categoria) => (
                        <Link
                          key={categoria.id}
                          to={`/equipos/categoria/${categoria.id}`}
                          className="block px-4 py-3 text-gray-300 hover:bg-yellow-600/10 hover:text-yellow-500 transition-colors duration-200 border-b border-gray-800 last:border-0"
                          onClick={() => setDropdownOpen(false)}
                        >
                          {categoria.nombre}
                        </Link>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-gray-500 text-sm text-center">Sin categorías</div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Botón Contacto */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Link
                to="/contacto"
                className="ml-4 px-6 py-2.5 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-bold rounded-full hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-yellow-500/25"
              >
                Contacto
              </Link>
            </motion.div>
          </nav>

          {/* Botón menú móvil */}
          <motion.button
            className="lg:hidden text-yellow-500 p-2"
            onClick={() => setIsOpen(!isOpen)}
            whileTap={{ scale: 0.9 }}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </motion.button>
        </div>

        {/* Menú Móvil */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden bg-black/98 backdrop-blur-md border-t border-yellow-600/20"
            >
              <nav className="flex flex-col py-4 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`px-6 py-3 font-medium transition-all duration-300 border-l-4 ${
                      location.pathname === item.to 
                        ? 'text-yellow-500 border-yellow-500 bg-yellow-600/10' 
                        : 'text-gray-300 border-transparent hover:text-yellow-500 hover:border-yellow-600/50'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}

                {/* Equipamiento Técnico - Móvil (DINÁMICO) */}
                <div className="px-6 py-3">
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="w-full flex items-center justify-between text-gray-300 font-medium"
                  >
                    <span>Equipamiento Técnico</span>
                    <motion.span
                      animate={{ rotate: dropdownOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown size={16} />
                    </motion.span>
                  </button>
                  
                  <AnimatePresence>
                    {dropdownOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-2 space-y-1 pl-4">
                          {loadingCats ? (
                            <div className="py-2 text-sm text-gray-500">Cargando...</div>
                          ) : categorias.length > 0 ? (
                            categorias.map((categoria) => (
                              <Link
                                key={categoria.id}
                                to={`/equipos/categoria/${categoria.id}`}
                                className="block py-2 text-sm text-gray-400 hover:text-yellow-500 transition-colors"
                                onClick={() => {
                                  setIsOpen(false);
                                  setDropdownOpen(false);
                                }}
                              >
                                {categoria.nombre}
                              </Link>
                            ))
                          ) : (
                            <div className="py-2 text-sm text-gray-500">Sin categorías</div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Botón Contacto - Móvil */}
                <div className="px-6 pt-4 pb-2">
                  <Link
                    to="/contacto"
                    className="block w-full text-center px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-bold rounded-lg hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300"
                    onClick={() => setIsOpen(false)}
                  >
                    Contacto
                  </Link>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;